import { ts } from '@shared/lib/diagTs';
import { rti } from '@features/rithmic/proto/rithmic.js';
import { getRelayOwnership } from '@core/realtime/relayOwnership';
import { rc } from './rithmicLog';
import { rememberRithmicAccountMeta } from './rithmicAccountMeta';

// ─── Rithmic post-login order-data sequence (FE-driven) ─────────────────────────────────────────────
// Per the Rithmic integration team's guidance, after a SUCCESSFUL login the browser must:
//   1. request the account list (to learn the account ids), then per account
//   2. SUBSCRIBE for order updates,
//   3. take a SINGLE `show_orders` snapshot of the current session's working orders (apply updates onto
//      it), and
//   4. back-fill HISTORICAL fills via `show_fill_history` — requested SERIALLY (the next request only
//      after the prior response completes), <= 30 days per request, bounded by the account creation time.
// Live + snapshot fills arrive as ExchangeOrderNotification (352, already ingested by the BE); historical
// fills arrive as ResponseShowFillHistory (3513) — the BE ingests both into the same Trade pipeline,
// deduped by the FILL-scoped external_id ("{basket_id}:{fill_id}" — sequence_number is ORDER-scoped and
// collapsed partial fills before 2026-09-12), so nothing double-counts.
// CATCH-UP POLL (2026-09-14 user ruling): on top of the one-shot login backfill, each subscribed account
// is re-asked for the last CATCHUP_WINDOW_SECONDS every CATCHUP_INTERVAL_MS, from the tab that owns the
// socket, through the SAME serial queue. That is a deliberate, bounded exception to "never poll" — see
// the resource-usage note in journal-backend/docs/integrations/rithmic.md §10.2.
// This module owns ONLY the OUTBOUND request orchestration; the relay routes the matching inbound
// responses here. Frames go out via the relay's `sendClient` (browser-owned socket; no credentials).
// Mirrors the CQG pattern. Fully isolated from CQG (separate relay singleton, hub, proto bundle).

// TEMPLATE IDS — CONFIRMED against the Rithmic dev-kit Reference Guide (RProtocolAPI 0.89.0.0) template-id
// table + SampleOrder.js, and verified live on Rithmic Test (302/308/320 round-tripped with no Reject).
export const RITHMIC_ORDER_TEMPLATE = {
  REQUEST_ACCOUNT_LIST: 302,
  RESPONSE_ACCOUNT_LIST: 303,
  REQUEST_SUBSCRIBE_FOR_ORDER_UPDATES: 308,
  RESPONSE_SUBSCRIBE_FOR_ORDER_UPDATES: 309,
  REQUEST_SHOW_ORDERS: 320,
  RESPONSE_SHOW_ORDERS: 321,
  REQUEST_SHOW_FILL_HISTORY: 3512,
  RESPONSE_SHOW_FILL_HISTORY: 3513,
} as const;

/** RequestAccountList.UserType.USER_TYPE_TRADER — a trader enumerates their own trading accounts. */
const USER_TYPE_TRADER = 3;

/** Rithmic caps show_fill_history at <= 30 days per request; we window the backfill accordingly. */
const THIRTY_DAYS_SECONDS = 30 * 24 * 60 * 60;
/** Safety cap on the number of 30-day windows per account (~5 years) so a bogus creation time can't
 *  enqueue thousands of requests. Older history than this is not auto-backfilled. */
const MAX_HISTORY_WINDOWS = 61;
/** show_fill_history max_record_count must be <= 10000 (dev-kit). */
const MAX_RECORD_COUNT = 10000;
/** Fallback lookback (seconds) when the account creation time is unknown/zero — one 30-day window. */
const DEFAULT_LOOKBACK_SECONDS = THIRTY_DAYS_SECONDS;
/** Catch-up cadence: re-ask every subscribed account for the recent window this often (2026-09-14). */
const CATCHUP_INTERVAL_MS = 30 * 60 * 1000;
/**
 * How often the elapsed-time check RUNS — not the cadence, which is {@link CATCHUP_INTERVAL_MS}.
 *
 * Every accepted login re-runs the whole order-data sequence, and the BACKEND's idle watchdog reopens
 * the browser socket every 15 minutes, so a 30-minute timer armed at login was cleared and re-armed
 * every 15 and could never reach 30 (dev 2026-09-15, eight hours of a connected tab, zero catch-ups).
 * The cadence therefore lives in {@link _lastCatchUpAt}, at MODULE scope so it survives both the
 * driver reset and the socket close.
 */
const CATCHUP_TICK_MS = 60 * 1000;
/** The window each catch-up asks for. WIDER than the interval so a fill landing on the boundary cannot
 *  fall between two polls; the overlap costs nothing because the BE dedups on the fill external_id. */
const CATCHUP_WINDOW_SECONDS = 35 * 60;

/**
 * Per-account backfill anchors from the BE (2026-08-25 split retrieval): account id → epoch-seconds
 * of the START OF THE DAY of the newest data the journal already holds. enqueueHistory floors each
 * account's window start at its anchor, so a RETURNING account resumes "from the same day,
 * including" instead of re-walking the whole creation-bounded range on every reconnect (up to 61
 * serial requests). Accounts absent from the map are NEW — they keep the full account-creation
 * backfill (user decision: "if it give us 5 years of history so take it, don't limit to 1 year").
 * Fetched from GET /api/v1/rithmic/history-anchors by the connect dialog; a missing/failed fetch
 * simply means today's full backfill — over-requesting is dedup-safe, under-requesting is not,
 * which is why the fallback is the WIDE window.
 */
let _historyAnchors: Record<string, number> = {};

/**
 * Epoch ms of the last catch-up this page fired. Module scope on purpose: a reconnect resets the driver
 * and re-arms its timer, so anything stored on the instance would restart the cadence every 15 minutes.
 * Seeded once at the first accepted login and never reset by a later one.
 */
let _lastCatchUpAt = 0;

export function setRithmicHistoryAnchors(anchors: Record<string, number>): void {
  _historyAnchors = anchors ?? {};
  rc.log(ts(), '[rithmic-order-data] history anchors set', { accounts: Object.keys(_historyAnchors).length });
}

/** What the driver needs from the relay: the FE-originated-frame send path (queues until the socket is open). */
export interface RithmicFrameSender {
  sendClient(body: Uint8Array): void;
  /**
   * Whether the browser socket is OPEN right now. `sendClient` QUEUES anything handed to it while the
   * socket is down and replays it on the NEXT socket ahead of the logon ack — carrying the previous
   * session's fcm/ib ids. Harmless for a one-shot backfill, wrong for a repeating poll, so the queue
   * pump asks first. Optional so a test double can omit it (undefined ⇒ "go ahead").
   */
  canSendNow?(): boolean;
}

interface HistoryWindow {
  accountId: string;
  startIndex: number;  // epoch seconds (index_format = "ssboe")
  finishIndex: number; // epoch seconds
}

/**
 * Per-login order-data driver. Reset on each accepted login (fresh fcm/ib ids, per-account dedupe set,
 * and empty history queue) so a reconnect re-runs the whole sequence cleanly and idempotently.
 */
class RithmicOrderDataDriver {
  private fcmId = '';
  private ibId = '';
  private readonly subscribed = new Set<string>();

  // GLOBAL serial history queue (Rithmic: "only send the next request after receiving the last response
  // for the prior request"). One show_fill_history request is in flight at a time, across ALL accounts.
  // This is also what satisfies their 2026-07-29 resource-usage warning — "throttling the number of
  // in-flight snapshot requests" — so DO NOT parallelise this queue for speed.
  private readonly historyQueue: HistoryWindow[] = [];
  private historyInFlight = false;
  private historySender: RithmicFrameSender | null = null;
  /** The periodic catch-up timer. Re-armed on every accepted login, cleared when the socket goes away. */
  private catchUpTimer: ReturnType<typeof setInterval> | null = null;

  /** ACCEPTED ResponseLogin → stash routing ids and kick the sequence off by requesting the account list. */
  onLoginAccepted(sender: RithmicFrameSender, ids: { fcmId: string; ibId: string }): void {
    this.fcmId = ids.fcmId;
    this.ibId = ids.ibId;
    this.subscribed.clear();
    this.historyQueue.length = 0;
    this.historyInFlight = false;
    this.historySender = sender;
    // Seed the cadence clock ONCE. Re-seeding here would restart it on every watchdog reconnect, which
    // is exactly the bug this replaced; the login backfill below already covers the recent window.
    if (_lastCatchUpAt === 0) _lastCatchUpAt = Date.now();
    this.armCatchUp();
    const body = rti.RequestAccountList.encode({
      templateId: RITHMIC_ORDER_TEMPLATE.REQUEST_ACCOUNT_LIST,
      fcmId: this.fcmId,
      ibId: this.ibId,
      userType: USER_TYPE_TRADER,
    }).finish();
    rc.log(ts(), '[rithmic-order-data] ⬆ RequestAccountList', { fcmId: this.fcmId, ibId: this.ibId });
    sender.sendClient(body);
  }

  /**
   * Each inbound ResponseAccountList (303). Rithmic sends ONE per account (the terminal message carries
   * only rp_code, no account_id). For every account_id seen the FIRST time: subscribe for order updates,
   * take the single show_orders snapshot, and enqueue the serial historical backfill bounded by the
   * account creation time. Idempotent per account (dedupe set).
   */
  onAccountList(sender: RithmicFrameSender, body: Uint8Array): void {
    let accountId = '';
    let creationSsboe = 0;
    try {
      const msg = rti.ResponseAccountList.decode(body);
      accountId = msg.accountId ?? '';
      creationSsboe = typeof msg.accountCreationSsboe === 'number' ? msg.accountCreationSsboe : 0;
      // Lift the display-worthy account-level fields (name / FCM / IB / currency) that Rithmic sends
      // in this frame but the FE otherwise discards, into the FE-only per-account meta store so the
      // connected dialog can show the friendly name + routing instead of just the raw id. Runs before
      // the per-account dedupe so a reconnect refreshes the meta. Never touches credentials.
      if (accountId) {
        rememberRithmicAccountMeta(accountId, {
          accountName: msg.accountName ?? '',
          fcmId: msg.fcmId ?? '',
          ibId: msg.ibId ?? '',
          currency: msg.accountCurrency ?? '',
        });
      }
    } catch {
      return; // unparseable — the raw frame is still forwarded to the BE by the relay
    }
    if (!accountId || this.subscribed.has(accountId)) return;
    this.subscribed.add(accountId);
    this.historySender = sender;
    rc.log(ts(), '[rithmic-order-data] account discovered - subscribing + snapshot + history', { accountId, creationSsboe });
    this.subscribeForOrderUpdates(sender, accountId);
    this.showOrdersSnapshot(sender, accountId);
    this.enqueueHistory(accountId, creationSsboe);
  }

  /**
   * Each inbound ResponseShowFillHistory (3513). Rithmic sends one message per historical fill (ingested
   * by the BE off the forwarded frame), then a TERMINAL message carrying rp_code — that's the completion
   * signal. Only on completion do we send the next window (serial requirement).
   */
  onFillHistoryResponse(body: Uint8Array): void {
    let rpCode: string[] = [];
    try {
      rpCode = rti.ResponseShowFillHistory.decode(body).rpCode ?? [];
    } catch {
      return; // an unparseable frame doesn't advance the queue; the request's real terminal will
    }
    if (rpCode.length === 0) return; // an intermediate fill row — wait for the terminal message
    rc.log(ts(), '[rithmic-order-data] ⬇ ResponseShowFillHistory complete', { rpCode: rpCode.join(',') });
    this.historyInFlight = false;
    this.pumpHistory();
  }

  private subscribeForOrderUpdates(sender: RithmicFrameSender, accountId: string): void {
    const body = rti.RequestSubscribeForOrderUpdates.encode({
      templateId: RITHMIC_ORDER_TEMPLATE.REQUEST_SUBSCRIBE_FOR_ORDER_UPDATES,
      fcmId: this.fcmId,
      ibId: this.ibId,
      accountId,
    }).finish();
    rc.log(ts(), '[rithmic-order-data] ⬆ RequestSubscribeForOrderUpdates', { accountId });
    sender.sendClient(body);
  }

  private showOrdersSnapshot(sender: RithmicFrameSender, accountId: string): void {
    // SINGLE snapshot of the current session's working orders (Rithmic RE-10, "current session order
    // data"). The journal doesn't render working orders yet, so ResponseShowOrders (321) is relayed to
    // the BE like any other frame; sending it satisfies the subscribe-THEN-snapshot pattern. Exactly ONE
    // per account — the snapshot itself is never polled (the catch-up asks show_fill_history, not this).
    // (For FILLS, the "update before snapshot" ordering Rithmic warns about is
    // already safe: the BE dedups by external_id, so a live 352 and a snapshot/history copy never
    // double-count regardless of arrival order.)
    const body = rti.RequestShowOrders.encode({
      templateId: RITHMIC_ORDER_TEMPLATE.REQUEST_SHOW_ORDERS,
      fcmId: this.fcmId,
      ibId: this.ibId,
      accountId,
    }).finish();
    rc.log(ts(), '[rithmic-order-data] ⬆ RequestShowOrders (single snapshot)', { accountId });
    sender.sendClient(body);
  }

  /** (Re)arm the periodic catch-up. Idempotent: a re-login replaces the previous timer, never stacks. */
  private armCatchUp(): void {
    this.stopCatchUp();
    this.catchUpTimer = setInterval(() => this.catchUpTick(), CATCHUP_TICK_MS);
  }

  /** Stop the periodic catch-up (relay stop / socket close). The next accepted login re-arms it. */
  stopCatchUp(): void {
    if (this.catchUpTimer === null) return;
    clearInterval(this.catchUpTimer);
    this.catchUpTimer = null;
  }

  /**
   * One catch-up tick: re-ask every subscribed account for the last {@link CATCHUP_WINDOW_SECONDS}.
   *
   * Deliberately STATELESS — each tick asks for a fixed recent window rather than resuming from a
   * cursor. A cursor advanced at send time would mark a range as covered that a dropped socket, an
   * error rp_code or a re-login never actually answered, and `onFillHistoryResponse` cannot tell those
   * apart (it treats ANY non-empty rp_code as terminal and carries no per-window correlation). Re-asking
   * a fixed window instead makes a lost answer cost one duplicate request, never a missed fill.
   */
  private catchUpTick(): void {
    // The cadence, checked against a module-scope clock so a re-arm cannot restart it.
    if (Date.now() - _lastCatchUpAt < CATCHUP_INTERVAL_MS) return;
    const sender = this.historySender;
    if (!sender) return;
    if (sender.canSendNow?.() === false) return;          // socket down — never queue a stale frame
    // Another tab owns the Rithmic socket; it relays and it runs this same tick. Fails OPEN for a lone tab.
    if (getRelayOwnership('rithmic').isPassive()) return;
    if (this.subscribed.size === 0) return;               // no accounts discovered yet
    if (this.historyQueue.length > 0) {                   // login backfill (or a prior tick) still draining
      rc.log(ts(), '[rithmic-order-data] catch-up skipped - history queue not drained',
        { queued: this.historyQueue.length });
      return;
    }
    _lastCatchUpAt = Date.now();
    const finishIndex = Math.floor(_lastCatchUpAt / 1000);
    const startIndex = finishIndex - CATCHUP_WINDOW_SECONDS;
    for (const accountId of this.subscribed) this.historyQueue.push({ accountId, startIndex, finishIndex });
    rc.log(ts(), '[rithmic-order-data] catch-up enqueued',
      { accounts: this.subscribed.size, startIndex, finishIndex });
    this.pumpHistory();
  }

  /** Compute the account's <= 30-day windows (creation → now, capped) and enqueue them, then pump. */
  private enqueueHistory(accountId: string, creationSsboe: number): void {
    const now = Math.floor(Date.now() / 1000);
    let start = creationSsboe > 0 ? creationSsboe : now - DEFAULT_LOOKBACK_SECONDS;
    if (start > now) start = now - DEFAULT_LOOKBACK_SECONDS; // guard against a bogus future creation time

    // RETURNING account (2026-08-25): resume from the day of the newest data the journal holds
    // instead of re-walking from account creation. Guards mirror the creation-time ones: only a
    // sane, past, newer-than-creation anchor may raise the start — anything else falls back to the
    // full (safe, dedup-idempotent) backfill.
    const anchor = _historyAnchors[accountId];
    if (typeof anchor === 'number' && anchor > start && anchor <= now) {
      rc.log(ts(), '[rithmic-order-data] history anchored - resuming from last held data', { accountId, anchor, creationSsboe });
      start = anchor;
    }

    const windows: HistoryWindow[] = [];
    let cursor = start;
    while (cursor <= now && windows.length < MAX_HISTORY_WINDOWS) {
      const finish = Math.min(cursor + THIRTY_DAYS_SECONDS - 1, now);
      windows.push({ accountId, startIndex: cursor, finishIndex: finish });
      cursor = finish + 1;
    }
    rc.log(ts(), '[rithmic-order-data] enqueue history', { accountId, windows: windows.length, fromSsboe: start, toSsboe: now });
    this.historyQueue.push(...windows);
    this.pumpHistory();
  }

  /** Send the next queued window IFF nothing is in flight (SERIAL — one request at a time, all accounts). */
  private pumpHistory(): void {
    if (this.historyInFlight) return;
    const sender = this.historySender;
    // Everything that could stop the send is checked BEFORE the dequeue: shifting first discarded the
    // head window whenever the sender was missing — invisible for a one-shot backfill, a permanently
    // missed range for a repeating poll. The socket is re-checked here and not only at the tick because
    // pumpHistory is also driven by an arriving terminal 3513 and by enqueueHistory.
    if (!sender) return;
    if (sender.canSendNow?.() === false) return;
    const w = this.historyQueue[0];
    if (!w) return;
    this.historyQueue.shift();
    this.historyInFlight = true;
    const body = rti.RequestShowFillHistory.encode({
      templateId: RITHMIC_ORDER_TEMPLATE.REQUEST_SHOW_FILL_HISTORY,
      fcmId: this.fcmId,
      ibId: this.ibId,
      accountId: w.accountId,
      indexFormat: 'ssboe',
      startIndex: w.startIndex,
      finishIndex: w.finishIndex,
      maxRecordCount: MAX_RECORD_COUNT,
    }).finish();
    rc.log(ts(), '[rithmic-order-data] ⬆ RequestShowFillHistory (serial window)',
      { accountId: w.accountId, startIndex: w.startIndex, finishIndex: w.finishIndex, remaining: this.historyQueue.length });
    sender.sendClient(body);
  }
}

// App-level singleton (mirrors the relay singleton — one login's sequence at a time).
let _driver: RithmicOrderDataDriver | null = null;
function driver(): RithmicOrderDataDriver {
  return (_driver ??= new RithmicOrderDataDriver());
}

/** Start the post-login order-data sequence (request the account list). Call on an ACCEPTED login. */
export function startRithmicOrderData(sender: RithmicFrameSender, ids: { fcmId: string; ibId: string }): void {
  driver().onLoginAccepted(sender, ids);
}

/** Feed one inbound ResponseAccountList (303) to the driver → subscribe + snapshot + enqueue history per account. */
export function onRithmicAccountList(sender: RithmicFrameSender, body: Uint8Array): void {
  driver().onAccountList(sender, body);
}

/** Feed one inbound ResponseShowFillHistory (3513) to the driver → advance the serial queue on completion. */
export function onRithmicFillHistoryResponse(body: Uint8Array): void {
  driver().onFillHistoryResponse(body);
}

/**
 * Stop the periodic catch-up — called when the browser socket goes away (relay stop or socket close) so
 * a dead session cannot keep ticking. The next accepted login re-arms it.
 */
export function stopRithmicOrderData(): void {
  driver().stopCatchUp();
}

/** Test seam: the module singleton is shared, so a suite can reset it between cases. */
export function _resetRithmicOrderDataForTests(): void {
  driver().stopCatchUp();
  _driver = null;
  _lastCatchUpAt = 0;
}
